import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner';
import { useActiveContext } from './useActiveContext';
import { config } from '../config';

interface ApiErrorResponse {
  error: string;
  description?: string;
}

/**
 * Shared "activate context" mutation, used by both the ContextSwitcher
 * dropdown and the `?ctx=` deep-link handler so the switch behaves
 * identically however it's triggered.
 *
 * POSTs to `/api/contexts/:id/activate` (which persists the user's
 * `last_active_context_id` server-side), then sets the local
 * active-context state — synchronously updating the axios
 * `X-Active-Context-Id` header + sessionStorage — and invalidates every
 * query so the whole context-scoped UI reloads under the new context.
 *
 * On failure (403/401: context not in the user's org, or no
 * ContextRead permission) it surfaces the backend's description as a
 * toast and leaves the current context untouched. Those error
 * descriptions deliberately don't contain "requested context", so the
 * global 403 interceptor in ActiveContextProvider won't clear a
 * perfectly valid current context just because a switch attempt failed.
 */
export function useActivateContext() {
  const queryClient = useQueryClient();
  const { setActiveContextId } = useActiveContext();

  return useMutation<undefined, AxiosError<ApiErrorResponse>, string>({
    mutationFn: async (contextId: string) => {
      await axios.post(
        `${config.backendApi}/api/contexts/${contextId}/activate`,
        {},
        { withCredentials: true },
      );
    },
    onSuccess: (_data, contextId) => {
      setActiveContextId(contextId);
      // currentUser carries activeContext; refetch it plus everything else.
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries();
    },
    onError: (err) => {
      toast.error(
        err.response?.data.description ??
          err.response?.data.error ??
          'Failed to switch context',
      );
    },
  });
}
